import { cn } from "@workspace/ui/lib/utils";
import Link from "next/link";
import type { ReactNode } from "react";
import type {
  RouteWithActive,
  SubRouteWithActive,
} from "@/components/dashboard/navigation";
import { Icons } from "@/components/icons";

function RouteIcon({
  hasUnread = false,
  icon,
}: {
  hasUnread?: boolean;
  icon?: ReactNode;
}) {
  return (
    <span className="relative flex size-10 shrink-0 items-center justify-center text-foreground group-hover:text-foreground">
      {icon}
      {hasUnread && (
        <span className="absolute top-2 right-2 size-2 rounded-full bg-[#FFD02B] ring-2 ring-background" />
      )}
    </span>
  );
}

type SidebarNavChildProps = {
  index: number;
  onNavigate?: () => void;
  show: boolean;
  sub: SubRouteWithActive;
};

function SidebarNavChild({
  index,
  onNavigate,
  show,
  sub,
}: SidebarNavChildProps) {
  return (
    <Link className="group/child block" href={sub.link} onClick={onNavigate}>
      <div
        className={cn(
          "mr-[15px] ml-5 flex h-8 items-center border-border border-l pl-3",
          "transition-all duration-200 ease-out motion-reduce:transition-none",
          show
            ? "translate-x-0 opacity-100"
            : "-translate-x-2 opacity-0 motion-reduce:translate-x-0 motion-reduce:opacity-100"
        )}
        style={{
          transitionDelay: show ? `${40 + index * 20}ms` : `${index * 20}ms`,
        }}
      >
        <span
          className={cn(
            "truncate font-medium text-muted-foreground text-sm transition-colors duration-200",
            "group-hover/child:text-foreground",
            sub.isActive && "text-foreground"
          )}
        >
          {sub.title}
        </span>
      </div>
    </Link>
  );
}

type SidebarNavChildrenProps = {
  onNavigate?: () => void;
  show: boolean;
  subs: SubRouteWithActive[];
};

function SidebarNavChildren({
  onNavigate,
  show,
  subs,
}: SidebarNavChildrenProps) {
  return (
    <div
      className={cn(
        "overflow-hidden transition-all duration-300 ease-out motion-reduce:transition-none",
        show ? "mt-1 max-h-96" : "max-h-0"
      )}
    >
      {subs.map((sub, index) => (
        <SidebarNavChild
          index={index}
          key={sub.link}
          onNavigate={onNavigate}
          show={show}
          sub={sub}
        />
      ))}
    </div>
  );
}

type SidebarNavItemProps = {
  expanded: boolean;
  hasUnread?: boolean;
  isNestedOpen: boolean;
  onNavigate?: () => void;
  onToggleNested: () => void;
  route: RouteWithActive;
};

export function SidebarNavItem({
  expanded,
  hasUnread = false,
  isNestedOpen,
  onNavigate,
  onToggleNested,
  route,
}: SidebarNavItemProps) {
  const hasChildren = Boolean(route.subs && route.subs.length > 0);
  const isAnySubActive =
    route.subs?.some((sub: SubRouteWithActive) => sub.isActive) ?? false;
  const isHighlighted = route.isActive || isAnySubActive;
  const shouldShowChildren = expanded && isNestedOpen && hasChildren;

  const handleChevronClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onToggleNested();
  };

  return (
    <div className="group">
      <Link
        aria-label={expanded ? undefined : route.title}
        className="group block"
        href={route.link}
        onClick={onNavigate}
        title={expanded ? undefined : route.title}
      >
        <div className="relative">
          <div
            className={cn(
              "ml-[1px] h-10 rounded-lg border border-transparent",
              "transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none",
              expanded ? "w-[calc(100%-2px)]" : "w-10",
              isHighlighted && "border-border bg-accent",
              !isHighlighted && "group-hover:bg-muted/70"
            )}
          />

          <div className="pointer-events-none absolute top-0 left-0 flex size-10 items-center justify-center">
            <RouteIcon hasUnread={hasUnread} icon={route.icon} />
          </div>

          {expanded && (
            <div className="pointer-events-none absolute top-0 right-1 left-10 flex h-10 items-center">
              <span
                className={cn(
                  "min-w-0 truncate font-medium text-base text-muted-foreground leading-none transition-colors duration-200",
                  "group-hover:text-foreground",
                  hasChildren ? "pr-1" : "",
                  isHighlighted && "text-foreground"
                )}
              >
                {route.title}
              </span>
              {hasChildren && (
                <button
                  aria-expanded={isNestedOpen}
                  aria-label={`Toggle ${route.title} submenu`}
                  className={cn(
                    "pointer-events-auto mr-2 ml-auto flex size-8 items-center justify-center",
                    "text-muted-foreground transition-all duration-200 hover:text-foreground",
                    isHighlighted && "text-foreground/60",
                    isNestedOpen && "rotate-180"
                  )}
                  onClick={handleChevronClick}
                  type="button"
                >
                  <Icons.chevronDown className="size-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </Link>

      {hasChildren && route.subs && (
        <SidebarNavChildren
          onNavigate={onNavigate}
          show={shouldShowChildren}
          subs={route.subs}
        />
      )}
    </div>
  );
}
